import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  DestroyRef,
  ViewEncapsulation,
  signal,
  computed,
  inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { GuestUser, User } from '@core/models';
import { Company } from '@core/models/company/company.model';
import {
  AUTH_SERVICE,
  NOTIFICATION_COOKIE_AUTH,
  NotificationLiveService,
  NotificationStore,
  PATIENT_SEARCH_NAVIGATION_SERVICE,
  xAuthToken
} from '@core/services';
import { DrawerStateService } from '@core/services/drawer-state.service';
import { BreakpointService } from '@shared/services';
import { Bind } from 'primeng/bind';
import { Button } from 'primeng/button';
import { Menubar } from 'primeng/menubar';
import { MenuItem } from 'primeng/api';
import { MainCompanySelectorComponent } from './main-company-selector/main-company-selector.component';
import {
  PatientSearch,
  PatientSearchSuggestion
} from './patient-search/patient-search';
import { UserProfileMenuComponent } from './user-profile-menu';
import { NotificationBell } from './notification-bell/notification-bell';

/* Remaining future work for the topbar:
My Messages — direct staff-to-staff messaging still lives only in the classic app.
 */

@Component({
  selector: 'app-topbar',
  templateUrl: 'app.topbar.component.html',
  styleUrls: ['app.topbar.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Styles need to reach into child components (app-patient-autocomplete's PrimeNG
  // internals) for the topbar-only search styling below. Emulated encapsulation's
  // shadow-DOM-lite scoping can't cross that boundary without ::ng-deep, so styles
  // are unscoped here and re-scoped manually via the `app-topbar-host` host class.
  encapsulation: ViewEncapsulation.None,
  host: { class: 'app-topbar-host' },
  providers: [BreakpointService],
  imports: [
    Bind,
    Button,
    Menubar,
    MainCompanySelectorComponent,
    NotificationBell,
    PatientSearch,
    UserProfileMenuComponent
  ]
})
export class AppTopBarComponent implements OnInit {
  private drawerStateService = inject(DrawerStateService);
  private route = inject(ActivatedRoute);
  private patientSearchNavigation = inject(PATIENT_SEARCH_NAVIGATION_SERVICE);
  private breakpointService = inject(BreakpointService);
  private notificationStore = inject(NotificationStore);
  private notificationLive = inject(NotificationLiveService);
  private notificationCookieAuth = inject(NOTIFICATION_COOKIE_AUTH);
  private auth = inject(AUTH_SERVICE);
  private destroyRef = inject(DestroyRef);

  // Expose drawer state to template
  protected drawerVisible = this.drawerStateService.visible;

  // The menubar itself carries no items; its #start and #end slots hold the topbar's
  // controls, and the user's own menu lives inside app-user-profile-menu.
  protected menuItems: MenuItem[] = [];

  /**
   * `.p-menubar-end`'s own CSS forces `margin-left: auto`, which on mobile (our menubar
   * is flex-col there) shrinks the #end slot to its content width and pushes it right,
   * cutting the search input's width instead of letting it span the row. A plain style
   * class can't override this since it's an inline style set via passthrough — and pt
   * styles are inline, so they'd otherwise beat any responsive Tailwind class regardless
   * of breakpoint. Bind it to the actual breakpoint instead of hardcoding one variant.
   *
   * `.p-menubar-start` needs the same width treatment: PrimeNG's own
   * `.p-menubar { align-items: center }` applies at every width, so on mobile the slot
   * shrinks to its content instead of spanning the row, and the search inside it cannot
   * reach full width. Only the width is forced here — how the slot's contents wrap onto
   * a second row is pure CSS in the template (flex-wrap), not driven off this signal.
   */
  protected menubarPt = computed(() => ({
    root: { style: { borderRadius: '0' } },
    start: this.breakpointService.isDesktop()
      ? {}
      : { style: { width: '100%' } },
    end: this.breakpointService.isDesktop()
      ? {}
      : { style: { marginLeft: '0', width: '100%' } }
  }));

  // User data signals
  protected user = signal<User | undefined>(undefined);
  protected guestUser = signal<GuestUser | undefined>(undefined);
  protected userType = signal<string>('');
  protected selectedCompany = signal<Company | undefined>(undefined);

  /**
   * Display name handed to the bell panel's compose dialog, which prefixes an outgoing
   * reply's body with the sender's name the way v1's `SendMessageCtrl` does. Derived the
   * same way the inbox page derives it from `AuthData`.
   */
  protected notificationSenderName = computed(() =>
    [this.user()?.firstName, this.user()?.lastName].filter(Boolean).join(' ')
  );

  // Computed signal for home link
  protected homeLink = computed(() =>
    this.userType() ? 'https://www.nymblsystems.com' : '/'
  );

  ngOnInit(): void {
    // Load user data from route
    this.route.data.subscribe((data) => {
      this.userType.set(data?.auth?.userType || '');

      if (this.userType()) {
        this.guestUser.set(data?.auth);
      } else {
        this.user.set(data?.auth?.currentUser);
      }

      if (data.auth) {
        this.selectedCompany.set(data.auth.currentCompany);
      }

      // Populate the bell's badge for a real (non-guest) user. Loaded here rather than in
      // the bell so the count is present whether or not the panel is ever opened, and only
      // once the resolver has produced a user — a guest has no notification inbox.
      const currentUser = this.user();
      if (!this.userType() && currentUser?.id) {
        this.notificationStore.loadLive();
        this.connectLiveUpdates(currentUser.id);
      }
    });

    // Stop receiving live updates on logout. Dropping the socket is the actual requirement —
    // the subscription lives server-side, so ignoring frames client-side would leave it
    // delivering to a logged-out session.
    this.auth.isLoggedIn$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isLoggedIn) => {
        if (!isLoggedIn) this.notificationLive.disconnect();
      });
  }

  /**
   * Opens the live socket, but only when this app has something that can authenticate it.
   *
   * There are two ways to reach an authenticated STOMP session and the client has to know
   * which one it has. nymbl2 puts its localStorage `X-Auth-Token` on the CONNECT frame. The
   * hybrid shell cannot read its token at all — it is an HttpOnly cookie — but the browser
   * sends that cookie on the handshake, and the server binds the verified identity to the
   * session there, so its CONNECT is accepted with no header.
   *
   * Neither case may be inferred from the other. CONNECT is the server's authentication
   * boundary: a frame it cannot authenticate is refused with an ERROR and the socket closed,
   * which stompjs answers by reconnecting on its delay forever. So connecting when we have
   * no means of authenticating buys a permanent retry loop against a server that will only
   * refuse it, while refusing to connect when the cookie would have worked silently gives up
   * live delivery. Hence an explicit flag rather than a guess.
   */
  private connectLiveUpdates(userId: number): void {
    const canAuthenticate =
      this.notificationCookieAuth || localStorage.getItem(xAuthToken) !== null;
    if (canAuthenticate) this.notificationLive.connect(userId);
  }

  toggleDrawer(): void {
    this.drawerStateService.toggle();
  }

  protected navigateToPatient(selection: PatientSearchSuggestion): void {
    this.patientSearchNavigation.navigate(selection);
  }
}
